import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import dayjs from 'dayjs';

const columns = [
  '',
  'Transaction ID',
  'Payment',
  'Type',
  'Status',
  'Amount',
  'Charge',
  'Instrument',
  'Customer',
  'Contact',
  'Created',
];

const PAYMENT_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'POINTS', label: 'Points' },
  { value: 'RAZORPAY', label: 'Razorpay' },
];

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

const formatPaymentInstrument = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (String(value).trim().toUpperCase() === 'DEFAULT') return 'UPI';
  return value;
};

const getGiftCardNames = (order) => {
  const cards = order?.giftCards;
  if (Array.isArray(cards) && cards.length) {
    const names = cards.map((card) => card?.productName).filter(Boolean);
    if (names.length) return names.join(', ');
  }

  const lines = order?.lines;
  if (!Array.isArray(lines) || !lines.length) return null;

  const names = lines.map((line) => line?.productName).filter(Boolean);
  return names.length ? names.join(', ') : null;
};

const MyTable = ({ transactions }) => {
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState('');

  const getRowId = (row, index) =>
    row?.id ||
    row?.transaction?.id ||
    row?._id ||
    row?.orderId ||
    `row-${index}`;

  const getTransactionRow = (row) => {
    const tx =
      row?.transaction && typeof row.transaction === 'object'
        ? row.transaction
        : {};

    return {
      ...tx,
      ...row,
      id: row?.id || tx?.id,
      paymentType: row?.paymentType ?? tx?.paymentType,
      entryType: row?.entryType ?? tx?.entryType,
      status: row?.status ?? tx?.status,
      amount: row?.amount ?? tx?.amount,
      orderId: row?.orderId ?? tx?.orderId,
      createdAt: row?.createdAt ?? tx?.createdAt,
      updatedAt: row?.updatedAt ?? tx?.updatedAt,
      userName:
        row?.userName ??
        row?.user?.name ??
        row?.user?.username ??
        row?.customerDetails?.customerId?.username,
      userEmail:
        row?.userEmail ??
        row?.user?.email ??
        row?.customerDetails?.customerId?.email,
      userPhone:
        row?.userPhone ??
        row?.user?.phone ??
        row?.customerDetails?.customerPhone,
      orders: row?.orders,
      paymentCharge:
        row?.paymentCharge ??
        (Array.isArray(row?.orders) ? row.orders[0]?.paymentCharge : undefined),
      paymentInstrument: row?.paymentType=="WALLET" ? "POINTS"  : formatPaymentInstrument(
        row?.paymentInstrument ??
          (Array.isArray(row?.orders) ? row.orders[0]?.paymentInstrument : undefined)
      ),
    };
  };

  const filteredTransactions = useMemo(() => {
    const list = Array.isArray(transactions) ? transactions : [];
    if (!paymentFilter) return list;

    return list.filter((row) => {
      const tx = getTransactionRow(row);
      const paymentType = String(tx?.paymentType || '').toUpperCase();

      if (paymentFilter === 'POINTS') {
        return paymentType === 'WALLET' || tx?.paymentInstrument === 'POINTS';
      }

      if (paymentFilter === 'RAZORPAY') {
        return paymentType === 'RAZORPAY';
      }

      return true;
    });
  }, [transactions, paymentFilter]);

  const formatPhone = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\s/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (/^91\d{10}$/.test(cleaned)) return `+${cleaned}`;
    if (cleaned.length === 10) return `+91 ${cleaned}`;
    return `+91 ${cleaned}`;
  };

  const ContactCell = ({ email, phone }) => (
    <Stack spacing={0.75} sx={{ minWidth: { xs: 140, sm: 180 }, maxWidth: 260 }}>
      <Stack direction="row" alignItems="flex-start" spacing={0.75}>
        <EmailOutlinedIcon
          sx={{ fontSize: 16, color: '#94a3b8', mt: 0.2, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            color: '#475569',
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {email || '—'}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <PhoneOutlinedIcon
          sx={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            color: '#0f172a',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            whiteSpace: 'nowrap',
          }}
        >
          {formatPhone(phone) || '—'}
        </Typography>
      </Stack>
    </Stack>
  );

  const CustomerCell = ({ name }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 100 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '10px',
          bgcolor: alpha('#11409B', 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <PersonOutlineOutlinedIcon sx={{ fontSize: 18, color: '#11409B' }} />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: '#0f172a',
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          wordBreak: 'break-word',
        }}
      >
        {name || '—'}
      </Typography>
    </Stack>
  );

  const getOrders = (row) => {
    if (Array.isArray(row?.orders) && row.orders.length > 0) {
      return row.orders;
    }
    return [];
  };

  const toggleRow = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const getDisplayStatus = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'pending_payment') return 'EXPIRED';
    if (normalized === 'expired') return 'FAILED';
    if (normalized === 'active') return 'PROCESSED';
    return status ? String(status).toUpperCase() : '—';
  };

  const getStatusStyles = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'success' || normalized === 'paid')
      return { color: '#16a34a', bg: alpha('#16a34a', 0.12) };
    if (
      normalized === 'failed' ||
      normalized === 'expired' ||
      normalized === 'pending' ||
      normalized === 'pending_payment'
    )
      return { color: '#dc2626', bg: alpha('#dc2626', 0.12) };
    return { color: '#64748b', bg: alpha('#64748b', 0.12) };
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '—';
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return '—';
    return `₹${numericAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD MMM YYYY, hh:mm A') : '—';
  };

  const StatusChip = ({ status }) => {
    const styles = getStatusStyles(status);
    return (
      <Chip
        label={getDisplayStatus(status)}
        size="small"
        sx={{
          height: 26,
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.04em',
          color: styles.color,
          bgcolor: styles.bg,
          border: `1px solid ${alpha(styles.color, 0.2)}`,
        }}
      />
    );
  };

  const DetailField = ({ label, value, highlight }) => (
    <Box sx={{ minWidth: { xs: '100%', sm: 160, md: 180 } }}>
      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
          fontWeight: 600,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: highlight ? '#0f172a' : '#334155',
          fontWeight: highlight ? 700 : 500,
          mt: 0.35,
          wordBreak: 'break-all',
        }}
      >
        {formatValue(value)}
      </Typography>
    </Box>
  );

  const renderGiftCardsTable = (giftCards = []) => {
    if (!giftCards.length) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}
        >
          Gift cards
        </Typography>
        <TableContainer
          sx={{
            borderRadius: '12px',
            border: `1px solid ${alpha('#11409B', 0.1)}`,
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                {['Gift card name', 'Amount', 'Validity', 'Card number'].map(
                  (h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        color: '#11409B',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        py: 1.25,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {giftCards.map((card, idx) => (
                <TableRow
                  key={card?.cardNumber || idx}
                  sx={{
                    '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                    '& td': { borderColor: alpha('#11409B', 0.06) },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                    {card?.productName || '—'}
                  </TableCell>
                  <TableCell>{card?.amount ?? '—'}</TableCell>
                  <TableCell sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                    {card?.validity || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#64748b',
                    }}
                  >
                    {card?.cardNumber || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderOrderLinesTable = (lines = [], order) => {
    if (!lines.length) return null;
    const giftCardNames = getGiftCardNames(order);
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}
        >
          Line items
        </Typography>
        <TableContainer
          sx={{
            borderRadius: '12px',
            border: `1px solid ${alpha('#11409B', 0.1)}`,
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#11409B', 0.04) }}>
                {['Product', 'Brand ID', 'Qty', 'Unit price', 'Line total', 'Denomination'].map(
                  (h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        color: '#11409B',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        py: 1.25,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, lineIdx) => (
                <TableRow
                  key={lineIdx}
                  sx={{
                    '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                    '& td': { borderColor: alpha('#11409B', 0.06) },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                    {line?.productName ||
                      order?.giftCards?.[lineIdx]?.productName ||
                      giftCardNames ||
                      line?.brandProductCode ||
                      line?.productCode ||
                      '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                    {line?.brandId || '—'}
                  </TableCell>
                  <TableCell>{line?.quantity ?? '—'}</TableCell>
                  <TableCell>{formatAmount(line?.unitPrice)}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#0d9488' }}>
                    {formatAmount(line?.lineTotal)}
                  </TableCell>
                  <TableCell>{line?.denomination || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderOrderCard = (order, orderIndex) => {
    const paymentStyles = getStatusStyles(order?.paymentStatus);
    return (
      <Box
        key={order?.id || `order-${orderIndex}`}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2,
          borderRadius: '16px',
          bgcolor: '#fff',
          border: `1px solid ${alpha('#11409B', 0.12)}`,
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: alpha('#11409B', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingBagOutlinedIcon sx={{ color: '#11409B', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                Order {orderIndex + 1}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#64748b', fontFamily: 'monospace' }}
              >
                {order?.id || '—'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={order?.paymentMode || '—'}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: alpha('#11409B', 0.08),
                color: '#11409B',
              }}
            />
            <Chip
              label={getDisplayStatus(order?.paymentStatus)}
              size="small"
              sx={{
                fontWeight: 700,
                color: paymentStyles.color,
                bgcolor: paymentStyles.bg,
              }}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          <DetailField label="Transaction ID" value={order?.transactionId} />
          <DetailField label="User ID" value={order?.userId} />
          <DetailField label="Currency" value={order?.currency} />
          <DetailField label="Denomination" value={order?.denomination} />
          <DetailField
            label="Total amount"
            value={formatAmount(order?.totalAmount)}
            highlight
          />
          <DetailField
            label="Transaction charge"
            value={formatAmount(order?.paymentCharge)}
            highlight
          />
          <DetailField
            label="Payment instrument"
            value={formatPaymentInstrument(order?.paymentInstrument)}
          />
          <DetailField
            label="Gift card name"
            value={getGiftCardNames(order)}
            highlight
          />
          <DetailField label="Product code" value={order?.productCode} />
          <DetailField label="Created" value={formatDateTime(order?.createdAt)} />
          <DetailField label="Updated" value={formatDateTime(order?.updatedAt)} />
        </Box>

        {renderGiftCardsTable(
          Array.isArray(order?.giftCards) ? order.giftCards : []
        )}
        {renderOrderLinesTable(order?.lines, order)}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: '1px solid',
        borderColor: alpha('#11409B', 0.08),
        bgcolor: '#fff',
        boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: `1px solid ${alpha('#11409B', 0.08)}`,
          bgcolor: alpha('#11409B', 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterListOutlinedIcon sx={{ color: '#11409B', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
            Payment type
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {PAYMENT_FILTER_OPTIONS.map((option) => {
            const isActive = paymentFilter === option.value;
            return (
              <Chip
                key={option.value || 'all'}
                label={option.label}
                clickable
                onClick={() => {
                  setPaymentFilter(option.value);
                  setExpandedRowId(null);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  height: 32,
                  bgcolor: isActive ? '#11409B' : alpha('#11409B', 0.08),
                  color: isActive ? '#fff' : '#11409B',
                  border: `1px solid ${isActive ? '#11409B' : alpha('#11409B', 0.18)}`,
                  '&:hover': {
                    bgcolor: isActive ? '#0f3580' : alpha('#11409B', 0.14),
                  },
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      {filteredTransactions.length === 0 ? (
        <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
          <Typography sx={{ color: '#64748b' }}>
            No transactions found for the selected payment type.
          </Typography>
        </Box>
      ) : (
    <TableContainer sx={{ overflow: 'auto' }}>
      <Table sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow
            sx={{
              bgcolor: alpha('#11409B', 0.04),
              '& th': {
                borderBottom: `1px solid ${alpha('#11409B', 0.1)}`,
                py: 2,
              },
            }}
          >
            {columns.map((column) => (
              <TableCell
                key={column || 'expand'}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#11409B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  ...(column === 'Contact' && { minWidth: { xs: 160, sm: 200 } }),
                  ...(column === 'Customer' && { minWidth: { xs: 120, sm: 140 } }),
                  display:
                    column === 'Type'
                      ? { xs: 'none', md: 'table-cell' }
                      : 'table-cell',
                }}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTransactions?.map((row, index) => {
            const tx = getTransactionRow(row);
            const rowId = getRowId(row, index);
            const orders = getOrders(row);
            const isExpanded = expandedRowId === rowId;
            const hasOrders = orders.length > 0;
            const status = tx?.status || tx?.orderStatus;

            return (
              <React.Fragment key={rowId}>
                <TableRow
                  sx={{
                    transition: 'background 0.2s',
                    bgcolor: isExpanded ? alpha('#11409B', 0.04) : 'transparent',
                    '&:hover': {
                      bgcolor: isExpanded
                        ? alpha('#11409B', 0.06)
                        : alpha('#11409B', 0.03),
                    },
                    '& td': {
                      borderBottom: `1px solid ${alpha('#11409B', 0.06)}`,
                      py: 1.75,
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ pl: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(rowId)}
                      disabled={!hasOrders}
                      aria-label={isExpanded ? 'Hide orders' : 'Show orders'}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        border: '1px solid',
                        borderColor: hasOrders
                          ? isExpanded
                            ? '#11409B'
                            : alpha('#11409B', 0.25)
                          : alpha('#94a3b8', 0.3),
                        bgcolor: isExpanded ? '#11409B' : '#fff',
                        color: isExpanded ? '#fff' : '#11409B',
                        transition: 'all 0.2s',
                        '&:hover': hasOrders
                          ? {
                              bgcolor: isExpanded ? '#0f3580' : alpha('#11409B', 0.08),
                            }
                          : {},
                        '&.Mui-disabled': {
                          bgcolor: '#f8fafc',
                          color: '#cbd5e1',
                        },
                      }}
                    >
                      {isExpanded ? (
                        <RemoveIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <AddIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#475569',
                        maxWidth: 120,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tx?.id || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tx?.paymentType || '—'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: alpha('#11409B', 0.08),
                        color: '#11409B',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: 'none', md: 'table-cell' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                      {tx?.entryType || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={status} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {formatAmount(tx?.amount ?? tx?.walletApplied)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                      {formatAmount(tx?.paymentCharge)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tx?.paymentInstrument || '—'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: alpha('#0d9488', 0.1),
                        color: '#0d9488',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <CustomerCell name={tx.userName} />
                  </TableCell>
                  <TableCell>
                    <ContactCell email={tx.userEmail} phone={tx.userPhone} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDateTime(tx?.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{
                      py: 0,
                      px: 0,
                      borderBottom: isExpanded
                        ? `1px solid ${alpha('#11409B', 0.08)}`
                        : 'none',
                      bgcolor: alpha('#11409B', 0.02),
                    }}
                  >
                    <Collapse in={isExpanded} timeout={300} unmountOnExit>
                      <Box
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderTop: `1px dashed ${alpha('#11409B', 0.15)}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, color: '#0f172a' }}
                          >
                            Linked orders
                          </Typography>
                          <Chip
                            label={`${orders.length} order${orders.length !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: alpha('#11409B', 0.1),
                              color: '#11409B',
                            }}
                          />
                        </Stack>

                        {hasOrders ? (
                          orders.map((order, orderIndex) =>
                            renderOrderCard(order, orderIndex)
                          )
                        ) : (
                          <Box
                            sx={{
                              py: 4,
                              textAlign: 'center',
                              borderRadius: '12px',
                              bgcolor: '#fff',
                              border: `1px dashed ${alpha('#11409B', 0.2)}`,
                            }}
                          >
                            <Typography sx={{ color: '#64748b' }}>
                              No order details available for this transaction.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
      )}
    </Box>
  );
};

export default MyTable;
